import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { FaArrowLeft } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SongEmbed from '../components/SongEmbed';
import YouTubeEmbed from '../components/YouTubeEmbed';

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  background-color: #E3E3E3;
  min-height: 100vh;
  border-radius: 12px;
`;

const ContentContainer = styled.div`
  width: 75%;
  margin: 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  overflow: hidden;
  padding-top: 80px;
  padding-bottom: 120px;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 40px 20px 80px;
  }
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: 5px 12px;
  border: 1px solid #2E2E2E;
  border-radius: 20px;
  color: #2E2E2E;
  font-family: 'Rubik', sans-serif;
  font-size: 0.9em;
  text-decoration: none;
  margin-bottom: 20px;
  width: fit-content;
  transition: all 0.2s;
  
  &:hover {
    background-color: #2E2E2E;
    color: #fff;
  }
  
  svg {
    margin-right: 6px;
    font-size: 0.9em;
  }
`;

const PostHeader = styled.div`
  margin-bottom: 40px;
`;

const PostTitle = styled.h1`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  font-size: 32px;
  margin: 0 0 20px;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const PostInfo = styled.div`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #6A7D8B;
`;

const Author = styled.span`
  margin-right: 8px;
  
  &:after {
    content: "•";
    margin-left: 8px;
  }
`;

const PostDate = styled.span``;

const FeaturedImageContainer = styled.div`
  margin: 24px 0;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const MarkdownContent = styled.div`
  font-family: 'Rubik', sans-serif;
  color: #2E2E2E;
  line-height: 1.7;
  
  /* Styling for markdown elements */
  h1, h2, h3, h4, h5, h6 {
    font-family: 'DM Serif Text', serif;
    color: #2E2E2E;
    margin: 24px 0 16px;
  }
  
  h1 {
    font-size: 28px;
  }
  
  h2 {
    font-size: 24px;
  }
  
  h3 {
    font-size: 20px;
  }
  
  p {
    margin: 16px 0;
  }
  
  a {
    color: #6A7D8B;
    text-decoration: underline;
  }
  
  blockquote {
    position: relative;
    margin: 30px 0;
    padding: 24px 32px;
    background: linear-gradient(135deg, rgba(106, 125, 139, 0.1), rgba(46, 46, 46, 0.05));
    border-radius: 12px;
    font-style: normal;
    color: #2E2E2E;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    
    /* Decorative bar on the left */
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 6px;
      background: linear-gradient(to bottom, #6A7D8B, #2E2E2E);
      border-radius: 2px;
    }
    
    /* Decorative quotation mark */
    &::after {
      content: '"';
      position: absolute;
      right: 20px;
      bottom: -20px;
      font-family: 'DM Serif Text', serif;
      font-size: 120px;
      font-style: normal;
      color: rgba(46, 46, 46, 0.1);
      line-height: 1;
    }
    
    /* Add top and bottom decorative lines */
    p {
      position: relative;
      z-index: 1;
      font-family: 'DM Serif Text', serif;
      font-size: 24px;
      color: #2E2E2E;
      font-weight: 700;
      line-height: 1.2;
      letter-spacing: 0;
    }
    
    /* Apply a subtle hover effect */
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
    }
  }
  
  ul, ol {
    padding-left: 20px;
    margin: 16px 0;
  }
  
  li {
    margin: 8px 0;
  }
  
  code {
    background-color: #f0f0f0;
    padding: 2px 4px;
    border-radius: 4px;
    font-family: monospace;
  }
  
  pre {
    background-color: #f0f0f0;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    
    code {
      background-color: transparent;
      padding: 0;
    }
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }
  
  /* Remove the first heading since we display it separately */
  h1:first-child {
    display: none;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #2E2E2E;
  font-family: 'Rubik', sans-serif;
  margin-top: 40px;
`;

// Add a custom renderer component that recognizes song embed patterns
const MarkdownWithSongEmbeds = ({ content }) => {
  // Regular expressions to match embed patterns:
  // [song-card:123] for a SongCard with ID 123
  // [song-list-item:456] for a SongListItem with ID 456
  // [youtube:abc123] for a YouTube video with ID abc123
  const songCardRegex = /\[song-card:([a-zA-Z0-9_-]+)\]/g;
  const songListItemRegex = /\[song-list-item:([a-zA-Z0-9_-]+)\]/g;
  const youtubeRegex = /\[youtube:([a-zA-Z0-9_-]+)\]/g;
  
  const processContent = () => {
    let processedContent = content;
    let embedComponents = [];
    
    // Process song cards
    let match;
    while ((match = songCardRegex.exec(content)) !== null) {
      embedComponents.push({
        index: match.index,
        component: <SongEmbed key={`song-${match[1]}`} songId={match[1]} type="card" />
      });
    }
    
    // Process song list items
    while ((match = songListItemRegex.exec(content)) !== null) {
      embedComponents.push({
        index: match.index,
        component: <SongEmbed key={`song-list-${match[1]}`} songId={match[1]} type="list-item" />
      });
    }
    
    // Process YouTube embeds
    while ((match = youtubeRegex.exec(content)) !== null) {
      embedComponents.push({
        index: match.index,
        component: <YouTubeEmbed key={`youtube-${match[1]}`} videoId={match[1]} />
      });
    }
    
    // Sort embeds by their position in the content
    embedComponents.sort((a, b) => a.index - b.index);
    
    // Replace all embed patterns with placeholders
    processedContent = processedContent
      .replace(songCardRegex, '⚡EMBED⚡')
      .replace(songListItemRegex, '⚡EMBED⚡')
      .replace(youtubeRegex, '⚡EMBED⚡');
    
    // Split content by embed placeholders and combine with components
    const parts = processedContent.split('⚡EMBED⚡');
    const result = [];
    
    parts.forEach((part, index) => {
      if (part) {
        result.push(
          <ReactMarkdown key={`md-${index}`} remarkPlugins={[remarkGfm]}>
            {part}
          </ReactMarkdown>
        );
      }
      if (embedComponents[index]) {
        result.push(embedComponents[index].component);
      }
    });
    
    return result;
  };

  return <MarkdownContent>{processContent()}</MarkdownContent>;
};

function BlogPostPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        const postResponse = await fetch(`/blog-posts/${postId}.md`);
        
        if (!postResponse.ok) {
          throw new Error('Blog post not found');
        }
        
        const text = await postResponse.text();
        
        // Extract front matter with a more flexible regex for line endings
        const frontMatterRegex = /---\s*([\s\S]*?)\s*---/;
        const frontMatterMatch = text.match(frontMatterRegex);
        
        if (frontMatterMatch && frontMatterMatch[1]) {
          const frontMatter = frontMatterMatch[1];
          const metadata = {};
          
          // Parse front matter
          frontMatter.split(/\r?\n/).forEach(line => {
            const [key, ...valueParts] = line.split(':');
            if (key && valueParts.length) {
              metadata[key.trim()] = valueParts.join(':').trim();
            }
          });
          
          // Process featured image path if it exists
          if (metadata.featuredImage) {
            metadata.featuredImage = `/images/blog-post-features/${metadata.featuredImage}`;
          }
          
          setPost({
            id: postId,
            ...metadata,
            content: text.replace(frontMatterRegex, '')
          });
        } else {
          throw new Error('Invalid blog post format');
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBlogPost();
  }, [postId]);

  // Parse date string to ensure consistent handling
  const parseDate = (dateString) => {
    // Try to handle different date formats consistently
    let dateObj;
    
    // Check if we have a date in MM/DD/YYYY format
    const usFormatMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usFormatMatch) {
      const [, month, day, year] = usFormatMatch;
      // Use ISO format for consistent parsing (YYYY-MM-DD)
      dateObj = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
    } else {
      // If we have YYYY-MM-DD format or other format, use standard parsing
      // but force UTC interpretation by appending Z or using Date.UTC
      if (dateString.includes('T')) {
        // If it has a time component already
        dateObj = new Date(dateString);
      } else {
        // If it's just a date without time
        dateObj = new Date(`${dateString}T00:00:00.000Z`);
      }
    }
    
    // Verify we got a valid date
    if (isNaN(dateObj.getTime())) {
      console.error('Failed to parse date:', dateString);
      return null;
    }
    
    return dateObj;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    try {
      const dateObj = parseDate(dateString);
      if (!dateObj) return 'Invalid date';
      
      return dateObj.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentContainer>
          <div>Loading blog post...</div>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (error || !post) {
    return (
      <PageContainer>
        <ContentContainer>
          <BackLink to="/blog">
            <FaArrowLeft />
            Blog
          </BackLink>
          <ErrorMessage>
            <h2>Error: {error || 'Blog post not found'}</h2>
            <p>The blog post you're looking for could not be found.</p>
          </ErrorMessage>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Helmet>
        <title>{post.title} | Tempo Stock Blog</title>
        <meta name="description" content={post.subtitle} />
      </Helmet>
      <ContentContainer>
        <BackLink to="/blog">
          <FaArrowLeft />
          Blog
        </BackLink>
        
        <PostHeader>
          <PostTitle>{post.title}</PostTitle>
          <PostMeta>
            <PostInfo>
              <Author>{post.author}</Author>
              <PostDate>{formatDate(post.date)}</PostDate>
            </PostInfo>
          </PostMeta>
        </PostHeader>
        
        {post.featuredImage && (
          <FeaturedImageContainer>
            <FeaturedImage src={post.featuredImage} alt={post.title} />
          </FeaturedImageContainer>
        )}
        
        <MarkdownContent>
          <MarkdownWithSongEmbeds content={post.content} />
        </MarkdownContent>
      </ContentContainer>
    </PageContainer>
  );
}

export default BlogPostPage; 